<script lang="ts">
  import { onMount } from 'svelte';
  import { memberListStore } from '@entities/member/store.svelte';
  import { deactivateAccount, reactivateAccount, revokeCredentials } from '@shared/api/accounts';
  import { cancelInvitation } from '@shared/api/members';
  import ConfirmDialog from '@shared/ui/ConfirmDialog.svelte';
  import { listOrganizations } from '@shared/api/admin';
  import { getAuth0EnvConfig } from '@shared/config/auth0';
  import { normalizeAccountRole } from '@shared/types/account';
  import type { Member } from '@shared/types/member';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import MemberTable from '@widgets/member-table/MemberTable.svelte';
  import RoleChangeDialog from '@features/member-role-change/RoleChangeDialog.svelte';
  import InviteDialog from '@features/member-invite/InviteDialog.svelte';
  import {
    canManageMembers,
    canManageMemberWithRole,
    canManageTenant,
    canViewMembers,
  } from '@shared/types/session';

  // 権限の UI ミラー (真実は backend)。ADR 20260605 org-scoped read:
  // - 閲覧: super_admin (全組織) / org_admin (自組織) = AdminReadOrgMembers
  // - 招待 / ロール変更: super_admin / org_admin = AdminInviteOrgMember 等
  // - 無効化 / 再有効化: super_admin 専用 = AdminManageGlobal
  let allowView = $derived(!!sessionStore.user && canViewMembers(sessionStore.user.role));
  let allowManageMembers = $derived(
    !!sessionStore.user && canManageMembers(sessionStore.user.role),
  );
  let allowManageTenant = $derived(
    !!sessionStore.user && canManageTenant(sessionStore.user.role),
  );

  // 招待 / ロール変更の供給先 org / client は裏で自動解決する。super_admin は
  // 全組織一覧の先頭、org_admin は自組織 (session.orgId) を使う。client は SPA 固定。
  let orgId = $state<string | null>(null);
  let clientId = $state<string | null>(null);
  let configError = $state<string | null>(null);
  let canInvite = $derived(allowManageMembers && !!orgId && !!clientId);

  // 招待モーダルの開閉。ロール選択 / 送信は InviteDialog (features) に委譲する。
  let showInvite = $state(false);

  // ロール変更モーダルの対象 (RoleChangeDialog を開く)。
  let roleTarget = $state<Member | null>(null);

  // 確認ダイアログ (無効化 / 再有効化 / 招待取消 / 認証情報削除)。window.confirm/alert を置換し、
  // 確認 → 実行 (loading) → 失敗時はダイアログ内エラー、を ConfirmDialog が内包する。
  type ConfirmAction = {
    title: string;
    message: string;
    confirmLabel: string;
    danger: boolean;
    run: () => Promise<void>;
  };
  let confirmAction = $state<ConfirmAction | null>(null);

  onMount(() => {
    // 一覧 read は閲覧権限 (super_admin / org_admin) を持つ場合のみ。member/readonly が
    // 叩くと backend が 403 を返すため、UI 側でも事前に弾く。
    if (allowView) void memberListStore.refresh();
    void resolveTargets();
  });

  // 招待 / ロール変更に必要な org_id / client_id を解決する。
  // - client_id: 招待は「招待された人がログインする SPA」名義で発行する。Auth0 Organizations の
  //   招待 API は org 有効な SPA の client_id を要求するため、VITE_AUTH0_CLIENT_ID を固定で使う。
  // - org_id: super_admin は全組織一覧の先頭 (単一 org 運用前提)。org_admin は自組織
  //   (session.orgId) を使う — listOrganizations() は AdminManageGlobal で org_admin だと
  //   403 になるため呼ばない。
  async function resolveTargets() {
    const user = sessionStore.user;
    if (!user || !canManageMembers(user.role)) return;
    clientId = getAuth0EnvConfig().clientId;

    if (canManageTenant(user.role)) {
      try {
        const orgs = await listOrganizations();
        orgId = orgs[0]?.id ?? null;
      } catch (err) {
        configError = err instanceof Error ? err.message : String(err);
        return;
      }
    } else {
      // org_admin: 自組織のみ。token claim 由来の orgId をそのまま使う。
      orgId = user.orgId;
    }

    if (!orgId) {
      configError = '招待先の Auth0 組織が解決できません。';
    }
  }

  /// この行のメンバーのロールを現在のユーザーが変更できるか。被害者保護ガードの UI ミラー:
  /// org_admin は自分を上回る super_admin メンバーを変更できない (backend も 403 で弾く)。
  function canChangeRoleOf(member: Member): boolean {
    const user = sessionStore.user;
    if (!user) return false;
    return canManageMemberWithRole(user.role, normalizeAccountRole(member.current_role));
  }

  function handleDeactivate(member: Member) {
    confirmAction = {
      title: '担当者を無効化',
      message: `"${member.display_name}" を無効化します。\nログインを停止し新規割り当ての候補から外れますが、履歴上の氏名は残り、再有効化で戻せます。`,
      confirmLabel: '無効化する',
      danger: true,
      run: async () => {
        await deactivateAccount(member.id);
        await memberListStore.refresh();
      },
    };
  }

  function handleReactivate(member: Member) {
    confirmAction = {
      title: '担当者を再有効化',
      message: `"${member.display_name}" を再有効化します。ログインを再び許可します。`,
      confirmLabel: '再有効化する',
      danger: false,
      run: async () => {
        await reactivateAccount(member.id);
        await memberListStore.refresh();
      },
    };
  }

  function handleCancelInvitation(member: Member) {
    // orgId は確認ダイアログを出す前に検証する。run (確認後) でチェックすると、
    // 解決失敗時でもダイアログが出てユーザーが押した後にエラーになる導線になるため。
    if (!orgId) {
      configError = configError ?? '招待先の組織が解決できていません。';
      return;
    }
    const resolvedOrgId = orgId;
    confirmAction = {
      title: '招待を取り消す',
      message: `${member.display_name} への招待を取り消します。担当者マスタの招待行も削除されます。`,
      confirmLabel: '招待を取り消す',
      danger: true,
      run: async () => {
        await cancelInvitation(member.id, resolvedOrgId);
        await memberListStore.refresh();
      },
    };
  }

  function handleRevokeCredentials(member: Member) {
    confirmAction = {
      title: '認証情報を削除',
      message: `"${member.display_name}" の認証情報を削除します。\nAuth0 ユーザーを削除しログインを恒久的に無効化します。商談・契約・履歴上の氏名は残ります。\nこの操作は元に戻せません。`,
      confirmLabel: '認証情報を削除する',
      danger: true,
      run: async () => {
        await revokeCredentials(member.id);
        await memberListStore.refresh();
      },
    };
  }
</script>

<header class="settings-header">
  <p class="eyebrow">管理 / メンバー</p>
  <h1>メンバー</h1>
</header>

<section class="panel">
  <div class="panel-head">
    <p class="muted">
      商談・契約・接点履歴などの「担当者」となる社内メンバーを管理します。招待すると Auth0
      の招待メールが送られ、初回ログインで有効になります。各行には Auth0
      上の在籍状態を併記します。Auth0 組織・ロールカタログそのものの編集は「組織 (Auth0・上級者向け)」で行います。
    </p>
    <button
      type="button"
      class="btn btn-primary"
      disabled={!canInvite}
      title={allowManageMembers
        ? canInvite
          ? ''
          : (configError ?? '招待先を解決中…')
        : '権限がありません'}
      onclick={() => (showInvite = true)}
    >
      + メンバーを招待
    </button>
  </div>

  {#if allowManageMembers && configError}
    <div class="error">{configError}</div>
  {/if}

  {#if !allowView}
    <p class="muted">メンバー一覧を表示する権限がありません。</p>
  {:else if memberListStore.loading && memberListStore.items.length === 0}
    <p class="muted">読み込み中…</p>
  {:else if memberListStore.error}
    <div class="error">{memberListStore.error}</div>
  {:else if memberListStore.items.length === 0}
    <p class="muted">メンバーがまだ登録されていません。</p>
  {:else}
    <MemberTable
      members={memberListStore.items}
      {allowManageMembers}
      {allowManageTenant}
      {canChangeRoleOf}
      currentAccountId={sessionStore.accountId}
      onChangeRole={(m) => (roleTarget = m)}
      onDeactivate={handleDeactivate}
      onReactivate={handleReactivate}
      onCancelInvitation={handleCancelInvitation}
      onRevokeCredentials={handleRevokeCredentials}
    />
  {/if}
</section>

{#if showInvite}
  <InviteDialog
    {orgId}
    {clientId}
    onClose={() => (showInvite = false)}
    onInvited={() => memberListStore.refresh()}
  />
{/if}

{#if roleTarget}
  <RoleChangeDialog
    member={roleTarget}
    {orgId}
    onClose={() => (roleTarget = null)}
    onChanged={() => memberListStore.refresh()}
  />
{/if}

{#if confirmAction}
  <ConfirmDialog
    title={confirmAction.title}
    message={confirmAction.message}
    confirmLabel={confirmAction.confirmLabel}
    danger={confirmAction.danger}
    onConfirm={confirmAction.run}
    onClose={() => (confirmAction = null)}
  />
{/if}

<style>
  .settings-header {
    margin-bottom: 16px;
  }
  .eyebrow {
    margin: 0 0 2px;
    color: var(--muted);
    font-weight: 700;
  }
  .panel {
    padding: 20px;
  }
  .panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }
  .panel-head p {
    margin: 0;
  }
  .error {
    color: var(--danger, #c83232);
    background: rgba(200, 50, 50, 0.06);
    padding: 8px;
    border-radius: 4px;
    margin: 8px 0;
  }
</style>
